// src/components/ControlBar.tsx

import React from 'react'
import { useTranslation } from 'react-i18next'
import { ReactComponent as PrevCatIcon } from '../assets/icons/prev-category1.svg'
import { ReactComponent as PrevIcon } from '../assets/icons/prev1.svg'
import { ReactComponent as PlayIcon } from '../assets/icons/play1.svg'
import { ReactComponent as PauseIcon } from '../assets/icons/pause1.svg'
import { ReactComponent as NextIcon } from '../assets/icons/next1.svg'
import { ReactComponent as NextCatIcon } from '../assets/icons/next-category1.svg'
import { ReactComponent as PhoneIcon } from '../assets/icons/phone.svg'
import { TIMING_CONSTANTS } from '../constants'

const { CONTROL_BAR_HEIGHT } = TIMING_CONSTANTS

interface NavigationState {
  hasPrev: boolean
  hasNext: boolean
  hasPrevCategory: boolean
  hasNextCategory: boolean
}

interface NavigationTitles {
  prevTitle: string
  nextTitle: string
  prevCategoryTitle: string
  nextCategoryTitle: string
}

interface ControlBarProps {
  navigation: NavigationState
  titles: NavigationTitles
  isPaused: boolean
  selectedLanguage: string | null
  selectedAvatar: string | null
  currentIndex: number
  onPrev: () => void
  onNext: () => void
  onPrevCategory: () => void
  onNextCategory: () => void
  onTogglePause: () => void
  onCallMe?: () => void
}

export default function ControlBar({
  navigation,
  titles,
  isPaused,
  selectedLanguage,
  selectedAvatar,
  currentIndex,
  onPrev,
  onNext,
  onPrevCategory,
  onNextCategory,
  onTogglePause,
  onCallMe,
}: ControlBarProps) {
  const { t } = useTranslation('common')
  const { hasPrev, hasNext, hasPrevCategory, hasNextCategory } = navigation
  const { prevTitle, nextTitle, prevCategoryTitle, nextCategoryTitle } = titles

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: `${CONTROL_BAR_HEIGHT}px`,
        background: 'rgba(0,0,0,1)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
        padding: '0 1rem',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0', justifyContent: 'center' }}>
        <button
          className={hasPrevCategory ? 'btn-icon' : 'btn-icon hidden'}
          data-tooltip={prevCategoryTitle}
          onClick={onPrevCategory}
        >
          <PrevCatIcon style={{ fill: '#A89B6F', width: 24, height: 24 }} />
        </button>

        <button
          className={hasPrev ? 'btn-icon' : 'btn-icon hidden'}
          data-tooltip={prevTitle}
          onClick={onPrev}
        >
          <PrevIcon style={{ fill: '#A89B6F', width: 24, height: 24 }} />
        </button>

        <button
          className={selectedLanguage !== null ? 'btn-icon' : 'btn-icon hidden'}
          data-tooltip={isPaused ? t('controlBar.tooltips.continue') : t('controlBar.tooltips.pause')}
          onClick={onTogglePause}
        >
          {isPaused ? (
            <PlayIcon style={{ fill: '#A89B6F', width: 24, height: 24 }} />
          ) : (
            <PauseIcon style={{ fill: '#A89B6F', width: 24, height: 24 }} />
          )}
        </button>

        <button
          className={hasNext && currentIndex !== 2 ? 'btn-icon' : 'btn-icon hidden'}
          data-tooltip={nextTitle}
          onClick={onNext}
        >
          <NextIcon style={{ fill: '#A89B6F', width: 24, height: 24 }} />
        </button>

        <button
          className={
            hasNextCategory && selectedAvatar !== null
              ? 'btn-icon'
              : 'btn-icon hidden'
          }
          data-tooltip={nextCategoryTitle}
          onClick={onNextCategory}
        >
          <NextCatIcon style={{ fill: '#A89B6F', width: 24, height: 24 }} />
        </button>
      </div>

      <button
        className={selectedLanguage !== null ? 'btn-icon call-me-btn' : 'btn-icon call-me-btn hidden'}
        data-tooltip={t('controlBar.tooltips.callMe')}
        onClick={onCallMe}
        style={{
          position: 'absolute',
          right: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: 'auto',
          paddingLeft: '12px',
          paddingRight: '16px',
        }}
      >
        <PhoneIcon style={{ fill: '#A89B6F', width: 20, height: 20 }} />
        <span className="call-me-text" style={{ color: '#A89B6F', fontSize: '14px', fontWeight: 500 }}>{t('buttons.callMe')}</span>
      </button>
    </div>
  )
}
